import React, { useState, useEffect } from 'react';
import { PluginConfigProps } from '../../../types/PluginInterface';
import { useFileManager } from '../../../hooks/useFileManager';
import { usePaymentApiKeys } from '../../../hooks/usePaymentApiKeys';
import {
  RentalConfig, RentalItem, RentalCategory, AvailabilityRange,
  SeasonalPrice, LongStayDiscount, VacationCapacity, VacationRules,
  VACATION_AMENITIES, CATEGORY_LABELS, AmenityDef, CustomBadge,
} from '../types';
import { api } from '../../../services/api';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../../../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../../../hooks/useTinyMceApiKey';

const formatLocalDate = (value: string) => {
  if (!value) return value;
  const parts = value.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts.map((p) => Number.parseInt(p, 10));
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      return new Date(y, m - 1, d).toLocaleDateString();
    }
  }
  return value;
};

const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url.split('?')[0].split('#')[0]);

const decodeHtml = (value: string) => {
  if (!value) return value;
  if (!value.includes('&lt;') && !value.includes('&gt;') && !value.includes('&amp;')) return value;
  if (typeof window === 'undefined') return value;
  const textarea = window.document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const toDateKey = (year: number, month: number, day: number) => {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const isDateInRange = (checkDate: Date, range: AvailabilityRange) => {
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return checkDate >= start && checkDate <= end;
};

// ── Planning calendar (admin view) ──────────────────────────────────────────
const PlanningCalendar: React.FC<{
  item: RentalItem;
  onBookingClick?: (range: AvailabilityRange, dateKey: string) => void;
}> = ({ item, onBookingClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: startDay }, (_, i) => i);
  const monthNames = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
  const availability = item.availability || [];

  const getRangeForDay = (day: number) => {
    const checkDate = new Date(year, month, day, 12, 0, 0);
    const matches = availability.filter((range) => isDateInRange(checkDate, range));
    if (!matches.length) return null;
    const booking = matches.find((r) => r.type === 'booking');
    return booking || matches[0];
  };

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <button className="btn btn-sm btn-link" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>&lt;</button>
        <div className="fw-bold small">{monthNames[month]} {year}</div>
        <button className="btn btn-sm btn-link" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>&gt;</button>
      </div>
      <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, minmax(32px, 1fr))', gap: '4px' }}>
        {["L","M","M","J","V","S","D"].map((d, i) => (
          <div key={i} className="text-center text-muted small">{d}</div>
        ))}
        {blanks.map((_, i) => <div key={`b-${i}`} />)}
        {daysArray.map((day) => {
          const range = getRangeForDay(day);
          const isBooking = range?.type === 'booking';
          const isBlocked = range?.type === 'blocked';
          const borderColor = isBooking ? '#28a745' : isBlocked ? '#dc3545' : '#e9ecef';
          const dateKey = toDateKey(year, month, day);
          return (
            <div
              key={day}
              className="d-flex align-items-center justify-content-center small"
              style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${borderColor}`, cursor: isBooking ? 'pointer' : 'default', backgroundColor: isBooking || isBlocked ? '#fff' : '#f8f9fa' }}
              title={range?.reason || (isBooking ? 'Réservation' : isBlocked ? 'Bloqué' : 'Disponible')}
              onClick={() => isBooking && range && onBookingClick?.(range, dateKey)}
            >{day}</div>
          );
        })}
      </div>
      <div className="mt-2 d-flex gap-3 justify-content-center small">
        <div className="d-flex align-items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #28a745', display: 'inline-block' }}></span> Réservation</div>
        <div className="d-flex align-items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #dc3545', display: 'inline-block' }}></span> Bloqué</div>
      </div>
    </div>
  );
};

// ── Composant principal ──────────────────────────────────────────────────────
export const RentalConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const currentConfig = config as RentalConfig;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || currentConfig.userId) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      if (ownerId) {
        onConfigChange({ ...currentConfig, userId: String(ownerId), ownerId: String(ownerId) });
      }
    } catch {}
  }, [currentConfig.userId]);

  type TabKey = 'general' | 'items' | 'tarifs' | 'planning' | 'payment' | 'avis' | 'custom' | 'confirmations';
  const [activeTab, setActiveTab] = useState<TabKey>('general');

  // Item form state
  const [currentItem, setCurrentItem] = useState<Partial<RentalItem>>({});
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemFormTab, setItemFormTab] = useState<'base' | 'capacite' | 'tarifs' | 'regles' | 'ical'>('base');

  // Seasonal prices temp state
  const [tempSeasonalPrices, setTempSeasonalPrices] = useState<SeasonalPrice[]>([]);
  const [tempLongStayDiscounts, setTempLongStayDiscounts] = useState<LongStayDiscount[]>([]);

  // Planning state
  const [selectedPlanningItem, setSelectedPlanningItem] = useState<RentalItem | null>(null);
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<AvailabilityRange | null>(null);
  const [_selectedBookingDate, setSelectedBookingDate] = useState<string | null>(null);
  const [planningReservations, setPlanningReservations] = useState<AvailabilityRange[]>([]);
  const [isLoadingReservations, setIsLoadingReservations] = useState(false);
  const [reservationsError, setReservationsError] = useState<string | null>(null);

  const { openFileManager } = useFileManager();
  const { hasStripeKey, hasPaypalKey, isLoading } = usePaymentApiKeys();
  const { apiKey: tinyMceApiKey, isLoading: tinyMceLoading, error: tinyMceError } = useTinyMceApiKey();

  const updateConfig = (newConfig: Partial<RentalConfig>) => onConfigChange({ ...currentConfig, ...newConfig });

  // ── Item CRUD ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentItem.id) {
      setTempSeasonalPrices(currentItem.seasonalPrices || []);
      setTempLongStayDiscounts(currentItem.longStayDiscounts || []);
    } else {
      setTempSeasonalPrices([]);
      setTempLongStayDiscounts([]);
    }
  }, [currentItem.id]);

  const handleSaveItem = () => {
    const itemToSave: RentalItem = {
      ...(currentItem as RentalItem),
      seasonalPrices: tempSeasonalPrices,
      longStayDiscounts: tempLongStayDiscounts,
    };

    let updatedItems: RentalItem[];
    if (editingItemId) {
      updatedItems = currentConfig.items.map(i => i.id === editingItemId ? { ...i, ...itemToSave } : i);
    } else {
      const newItem: RentalItem = {
        ...itemToSave,
        id: `rental_${Date.now()}`,
        name: itemToSave.name || 'Nouveau Bien',
        category: itemToSave.category || 'villa',
        description: itemToSave.description || '',
        images: itemToSave.images || [],
        pricePerDay: itemToSave.pricePerDay || 0,
        availability: itemToSave.availability || [],
      };
      updatedItems = [...(currentConfig.items || []), newItem];
    }
    updateConfig({ items: updatedItems });
    setCurrentItem({});
    setEditingItemId(null);
    setShowItemForm(false);
  };

  const handleEditItem = (item: RentalItem) => {
    setEditingItemId(item.id);
    setCurrentItem(item);
    setItemFormTab('base');
    setShowItemForm(true);
  };

  const handleDeleteItem = (itemId: string) => {
    if (!window.confirm("Supprimer ce bien ?")) return;
    if (currentConfig.userId) {
      api.delete(`/rental-reservations/item/${itemId}`).catch(() => {});
    }
    updateConfig({ items: currentConfig.items.filter(i => i.id !== itemId) });
    if (editingItemId === itemId) { setShowItemForm(false); setEditingItemId(null); setCurrentItem({}); }
    if (selectedPlanningItem?.id === itemId) setSelectedPlanningItem(null);
  };

  const handleAddItem = () => {
    setCurrentItem({ category: 'villa', images: [], amenities: [], capacity: { guests: 2, bedrooms: 1, beds: 1, bathrooms: 1 } });
    setEditingItemId(null);
    setItemFormTab('base');
    setShowItemForm(true);
  };

  const openMediaSelector = () => {
    openFileManager({
      onFileSelect: (file) => {
        setCurrentItem((prev) => ({ ...prev, images: [...(prev.images || []), file.url] }));
      },
      type: 'all'
    });
  };

  const toggleAmenity = (amenityId: string) => {
    setCurrentItem((prev) => {
      const current = prev.amenities || [];
      return {
        ...prev,
        amenities: current.includes(amenityId)
          ? current.filter(a => a !== amenityId)
          : [...current, amenityId],
      };
    });
  };

  const updateCapacity = (field: keyof VacationCapacity, val: number) => {
    setCurrentItem((prev) => ({ ...prev, capacity: { ...(prev.capacity || { guests: 2, bedrooms: 1, beds: 1, bathrooms: 1 }), [field]: val } }));
  };

  const updateRules = (field: keyof VacationRules, val: any) => {
    setCurrentItem((prev) => ({ ...prev, rules: { ...(prev.rules || {}), [field]: val } }));
  };

  // ── Seasonal prices ────────────────────────────────────────────────────────
  const addSeasonalPrice = () => {
    setTempSeasonalPrices(prev => [...prev, { id: `sp_${Date.now()}`, label: '', startDate: '', endDate: '', pricePerDay: 0 }]);
  };
  const updateSeasonalPrice = (idx: number, field: keyof SeasonalPrice, val: any) => {
    setTempSeasonalPrices(prev => prev.map((sp, i) => i === idx ? { ...sp, [field]: val } : sp));
  };
  const removeSeasonalPrice = (idx: number) => {
    setTempSeasonalPrices(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Long-stay discounts ────────────────────────────────────────────────────
  const addLongStayDiscount = () => {
    setTempLongStayDiscounts(prev => [...prev, { minNights: 7, discountPercent: 10 }]);
  };
  const updateLongStayDiscount = (idx: number, field: keyof LongStayDiscount, val: number) => {
    setTempLongStayDiscounts(prev => prev.map((d, i) => i === idx ? { ...d, [field]: val } : d));
  };
  const removeLongStayDiscount = (idx: number) => {
    setTempLongStayDiscounts(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Planning ───────────────────────────────────────────────────────────────
  useEffect(() => { setSelectedBooking(null); setSelectedBookingDate(null); }, [selectedPlanningItem?.id]);

  useEffect(() => {
    if (!selectedPlanningItem || !currentConfig.userId) { setPlanningReservations([]); return; }
    let cancelled = false;
    setIsLoadingReservations(true);
    setReservationsError(null);
    const params = new URLSearchParams({ rentalItemId: selectedPlanningItem.id });
    api.get<any>(`/rental-reservations/owner/${currentConfig.userId}?${params.toString()}`)
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray((response as any).reservations) ? (response as any).reservations : (response as any);
        const mapped: AvailabilityRange[] = (list || []).map((res: any) => ({
          startDate: res.startDate, endDate: res.endDate, type: 'booking' as const,
          reason: res.reason || 'Réservation', bookingId: res.id ? String(res.id) : undefined,
          bookingDetails: res.bookingDetails || { totalPrice: res.totalPrice, currency: res.currency, paymentMethod: res.paymentMethod, transactionId: res.transactionId, createdAt: res.createdAt }
        }));
        setPlanningReservations(mapped);
      })
      .catch(() => { if (!cancelled) setReservationsError("Impossible de charger les réservations."); })
      .finally(() => { if (!cancelled) setIsLoadingReservations(false); });
    return () => { cancelled = true; };
  }, [selectedPlanningItem?.id, currentConfig.userId]);

  const mergeAvailability = (item: RentalItem, reservations: AvailabilityRange[]) => {
    const seen = new Set<string>();
    return [...(item.availability || []), ...reservations].filter((r) => {
      const key = `${r.type}-${r.startDate}-${r.endDate}-${r.bookingId || ''}-${r.reason || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleAddBlock = () => {
    if (!selectedPlanningItem || !blockStart || !blockEnd) return;
    if (new Date(blockStart) > new Date(blockEnd)) { alert("La date de fin doit être après la date de début."); return; }
    const newBlock: AvailabilityRange = { startDate: blockStart, endDate: blockEnd, type: 'blocked', reason: blockReason || 'Indisponible' };
    const updated = { ...selectedPlanningItem, availability: [...(selectedPlanningItem.availability || []), newBlock] };
    updateConfig({ items: currentConfig.items.map(i => i.id === selectedPlanningItem.id ? updated : i) });
    setSelectedPlanningItem(updated);
    setBlockStart(''); setBlockEnd(''); setBlockReason('');
  };

  const handleDeleteBlock = (index: number) => {
    if (!selectedPlanningItem) return;
    const newAvailability = [...(selectedPlanningItem.availability || [])];
    newAvailability.splice(index, 1);
    const updated = { ...selectedPlanningItem, availability: newAvailability };
    updateConfig({ items: currentConfig.items.map(i => i.id === selectedPlanningItem.id ? updated : i) });
    setSelectedPlanningItem(updated);
  };

  const planningItem = selectedPlanningItem
    ? { ...selectedPlanningItem, availability: mergeAvailability(selectedPlanningItem, planningReservations) }
    : null;
  const mergedAvailability = planningItem?.availability || [];

  // ── TinyMCE helper ─────────────────────────────────────────────────────────
  const renderEditor = (value: string, onChange: (v: string) => void, rows = 3) => {
    if (tinyMceApiKey && !tinyMceLoading) {
      return (
        <Editor
          apiKey={tinyMceApiKey}
          value={decodeHtml(value)}
          init={withTabLink({
            height: rows * 40 + 80, menubar: false,
            plugins: ['autolink', 'lists', 'link', 'image', 'searchreplace', 'code', 'fullscreen', 'insertdatetime', 'table', 'wordcount'],
            toolbar: 'undo redo | bold italic underline | bullist numlist | link image | code fullscreen',
            entity_encoding: 'raw', language: 'fr', branding: false,
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }',
            paste_data_images: true, paste_as_text: false, paste_retain_style_properties: 'all',
            valid_elements: '*[*]',
          })}
          onEditorChange={onChange}
        />
      );
    }
    return (
      <textarea
        className="form-control"
        rows={rows}
        value={decodeHtml(value)}
        onChange={e => onChange(e.target.value)}
      />
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-3 bg-white rounded shadow-sm" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
      <ul className="nav nav-pills mb-4 flex-wrap gap-1">
        {([
          ['general', '⚙️ Général'],
          ['items', '🏠 Biens'],
          ['tarifs', '💶 Tarifs & Conditions'],
          ['planning', '📅 Planning'],
          ['payment', '💳 Paiement'],
          ['custom', '🎨 Personnalisation'],
          ['confirmations', '📧 Confirmations'],
          ['avis', '⭐ Avis'],
        ] as [TabKey, string][]).map(([key, label]) => (
          <li key={key} className="nav-item">
            <button className={`nav-link ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>{label}</button>
          </li>
        ))}
      </ul>

      {/* ─── GÉNÉRAL ──────────────────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label fw-semibold">Titre</label>
            <input type="text" className="form-control" value={currentConfig.title} onChange={e => updateConfig({ title: e.target.value })} />
          </div>
          <div className="col-12">
            <label className="form-label fw-semibold">Description</label>
            {tinyMceError && <div className="alert alert-warning py-2 small mb-2">{tinyMceError}</div>}
            {tinyMceLoading && <div className="text-muted small mb-2">Chargement éditeur...</div>}
            {renderEditor(currentConfig.description || '', v => updateConfig({ description: v }), 3)}
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Affichage</label>
            <select className="form-select" value={currentConfig.displayMode} onChange={e => updateConfig({ displayMode: e.target.value as 'list' | 'grid' })}>
              <option value="grid">Grille (Cartes)</option>
              <option value="list">Liste (Compact)</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Mode réservation</label>
            <select className="form-select" value={currentConfig.bookingMode || 'instant'} onChange={e => updateConfig({ bookingMode: e.target.value as 'instant' | 'request' })}>
              <option value="instant">⚡ Instantanée (auto-confirm)</option>
              <option value="request">📨 Sur demande (validation manuelle)</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Couleur principale</label>
            <input type="color" className="form-control form-control-color w-100" value={currentConfig.primaryColor || '#e63946'} onChange={e => updateConfig({ primaryColor: e.target.value })} />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Fond plugin</label>
            <div className="d-flex gap-2 align-items-center">
              <input type="color" className="form-control form-control-color" value={currentConfig.backgroundColor === 'transparent' ? '#ffffff' : (currentConfig.backgroundColor || '#ffffff')} disabled={currentConfig.backgroundColor === 'transparent'} onChange={e => updateConfig({ backgroundColor: e.target.value })} />
              <div className="form-check mb-0">
                <input className="form-check-input" type="checkbox" id="bgTransparent" checked={currentConfig.backgroundColor === 'transparent'} onChange={e => updateConfig({ backgroundColor: e.target.checked ? 'transparent' : '#ffffff' })} />
                <label className="form-check-label small" htmlFor="bgTransparent">Transparent</label>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Texte bouton</label>
            <input type="text" className="form-control" value={currentConfig.itemButtonText || 'Voir & Réserver'} onChange={e => updateConfig({ itemButtonText: e.target.value })} />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Couleur bouton</label>
            <input type="color" className="form-control form-control-color w-100" value={currentConfig.itemButtonBackgroundColor || '#e63946'} onChange={e => updateConfig({ itemButtonBackgroundColor: e.target.value })} />
          </div>
        </div>
      )}

      {/* ─── BIENS ────────────────────────────────────────────────────────── */}
      {activeTab === 'items' && (
        <div className="d-flex flex-column gap-3">
          <div className="card border-light bg-light">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Liste des biens ({currentConfig.items?.length || 0})</h6>
              <button className="btn btn-sm btn-primary" onClick={handleAddItem}>+ Ajouter un bien</button>
            </div>
            <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: 220 }}>
              {!currentConfig.items?.length && <div className="p-3 text-center text-muted small">Aucun bien ajouté.</div>}
              {currentConfig.items?.map(item => (
                <div key={item.id} className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${editingItemId === item.id ? 'active' : ''}`}>
                  <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => handleEditItem(item)} className="me-2">
                    <div className="fw-bold text-truncate">{item.name}</div>
                    <div className="small opacity-75">{CATEGORY_LABELS[item.category] || item.category} — {item.pricePerDay} {currentConfig.currency}/nuit</div>
                  </div>
                  <button className="btn btn-sm btn-light text-danger border py-1 px-2 flex-shrink-0" onClick={e => { e.stopPropagation(); handleDeleteItem(item.id); }}>Supprimer</button>
                </div>
              ))}
            </div>
          </div>

          {showItemForm && (
            <div className="card border shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{editingItemId ? `Modifier : ${currentItem.name}` : 'Nouveau bien'}</h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowItemForm(false)}>✕</button>
              </div>
              <div className="card-body">
                {/* Sub-tabs item form */}
                <ul className="nav nav-tabs mb-3 flex-wrap gap-1">
                  {([['base','📝 Infos'],['capacite','👥 Capacité & Équip.'],['tarifs','💶 Tarifs'],['regles','📋 Règles'],['ical','🔗 iCal']] as [typeof itemFormTab, string][]).map(([k, l]) => (
                    <li key={k} className="nav-item">
                      <button className={`nav-link py-1 px-2 ${itemFormTab === k ? 'active' : ''}`} onClick={() => setItemFormTab(k)}>{l}</button>
                    </li>
                  ))}
                </ul>

                {/* ── INFOS DE BASE ── */}
                {itemFormTab === 'base' && (
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small">Nom du bien</label>
                      <input type="text" className="form-control" placeholder="Ex: Villa Vue Mer" value={currentItem.name || ''} onChange={e => setCurrentItem(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Catégorie</label>
                      <select className="form-select" value={currentItem.category || 'villa'} onChange={e => setCurrentItem(p => ({ ...p, category: e.target.value as RentalCategory }))}>
                        <option value="villa">🏖️ Villa</option>
                        <option value="maison">🏡 Maison</option>
                        <option value="appartement">🏢 Appartement</option>
                        <option value="gite">🌾 Gîte</option>
                        <option value="chalet">🏔️ Chalet</option>
                        <option value="studio">🏠 Studio</option>
                        <option value="glamping">⛺ Glamping</option>
                        <option value="camping">🏕️ Camping</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Badge</label>
                      <select className="form-select" style={{ height: 38 }} value={currentItem.badge || ''} onChange={e => setCurrentItem(p => ({ ...p, badge: e.target.value || undefined }))}>
                        <option value="">Aucun</option>
                        <option value="new">🆕 Nouveauté</option>
                        <option value="coup-de-coeur">❤️ Coup de cœur</option>
                        <option value="derniere-minute">⚡ Dernière minute</option>
                        <option value="exclusivite">🌟 Exclusivité</option>
                        {(currentConfig.customBadges || []).map(b => (
                          <option key={b.id} value={b.id}>{b.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Description</label>
                      {renderEditor(currentItem.description || '', v => setCurrentItem(p => ({ ...p, description: v })), 4)}
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Adresse (pour Google Maps)</label>
                      <input type="text" className="form-control" placeholder="Adresse complète..." value={currentItem.address || ''} onChange={e => setCurrentItem(p => ({ ...p, address: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Photos & Vidéos</label>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {currentItem.images?.map((img, i) => (
                          <div key={i} className="position-relative">
                            {isVideoUrl(img)
                              ? <video src={img} className="rounded shadow-sm" style={{ width: 60, height: 60, objectFit: 'cover' }} muted playsInline />
                              : <img src={img} alt="aperçu" className="rounded shadow-sm" style={{ width: 60, height: 60, objectFit: 'cover' }} />
                            }
                            <button className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 rounded-circle" style={{ width: 18, height: 18, lineHeight: 1, fontSize: 10, transform: 'translate(30%, -30%)' }} onClick={() => setCurrentItem(p => ({ ...p, images: (p.images || []).filter((_, j) => j !== i) }))}>×</button>
                          </div>
                        ))}
                        <button className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }} title="Ajouter photo/vidéo" onClick={openMediaSelector}>📷🎬</button>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Vidéo YouTube/Vimeo (présentation)</label>
                      <div className="input-group">
                        <input type="text" className="form-control" style={{ height: 38 }} placeholder="https://www.youtube.com/watch?v=..." value={currentItem.videoUrl || ''} onChange={e => setCurrentItem(p => ({ ...p, videoUrl: e.target.value }))} />
                        <button className="btn btn-outline-secondary" style={{ height: 38 }} title="Choisir une vidéo depuis le gestionnaire" onClick={() => openFileManager({ onFileSelect: (file) => setCurrentItem(p => ({ ...p, videoUrl: file.url })), type: 'video' as any })}>🎬</button>
                        {currentItem.videoUrl && (
                          <button className="btn btn-outline-danger" style={{ height: 38 }} onClick={() => setCurrentItem(p => ({ ...p, videoUrl: undefined }))} title="Effacer">×</button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── CAPACITÉ & ÉQUIPEMENTS ── */}
                {itemFormTab === 'capacite' && (
                  <div>
                    <h6 className="border-bottom pb-2 mb-3">Capacité d'accueil</h6>
                    <div className="row g-3 mb-4">
                      {([
                        ['guests', '👥 Voyageurs max'],
                        ['bedrooms', '🛏️ Chambres'],
                        ['beds', '🛌 Lits'],
                        ['bathrooms', '🚿 Salles de bain'],
                      ] as [keyof VacationCapacity, string][]).map(([field, label]) => (
                        <div key={field} className="col-6 col-md-3">
                          <label className="form-label small">{label}</label>
                          <input type="number" className="form-control text-center" min={0} value={(currentItem.capacity as any)?.[field] || 0} onChange={e => updateCapacity(field, parseInt(e.target.value) || 0)} />
                        </div>
                      ))}
                    </div>

                    <h6 className="border-bottom pb-2 mb-3">Équipements</h6>
                    <div className="row g-2">
                      {[...VACATION_AMENITIES, ...(currentConfig.customAmenities || [])].map(a => (
                        <div key={a.id} className="col-6 col-md-4 col-lg-3">
                          <div
                            className={`p-2 rounded border d-flex align-items-center gap-2 small ${(currentItem.amenities || []).includes(a.id) ? 'border-primary bg-primary bg-opacity-10 text-primary fw-semibold' : 'border-light bg-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleAmenity(a.id)}
                          >
                            <span>{a.icon}</span>
                            <span>{a.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── TARIFS ── */}
                {itemFormTab === 'tarifs' && (
                  <div>
                    <div className="row g-3 mb-4">
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Prix de base / nuit</label>
                        <div className="input-group">
                          <input type="number" className="form-control" value={currentItem.pricePerDay || ''} onChange={e => setCurrentItem(p => ({ ...p, pricePerDay: parseFloat(e.target.value) || 0 }))} />
                          <span className="input-group-text">{currentConfig.currency}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Prix weekend (ven/sam)</label>
                        <div className="input-group">
                          <input type="number" className="form-control" placeholder="Si différent" value={currentItem.weekendPricePerDay || ''} onChange={e => setCurrentItem(p => ({ ...p, weekendPricePerDay: parseFloat(e.target.value) || undefined }))} />
                          <span className="input-group-text">{currentConfig.currency}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Frais de ménage</label>
                        <div className="input-group">
                          <input type="number" className="form-control" placeholder="0" value={currentItem.cleaningFee || ''} onChange={e => setCurrentItem(p => ({ ...p, cleaningFee: parseFloat(e.target.value) || undefined }))} />
                          <span className="input-group-text">{currentConfig.currency}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Réduction dernière minute (J-3)</label>
                        <div className="input-group">
                          <input type="number" className="form-control" placeholder="Ex: 15" min={0} max={80} value={currentItem.lastMinuteDiscountPercent || ''} onChange={e => setCurrentItem(p => ({ ...p, lastMinuteDiscountPercent: parseFloat(e.target.value) || undefined }))} />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>
                    </div>

                    <h6 className="border-bottom pb-2 mb-2">Tarifs saisonniers</h6>
                    <div className="alert alert-info py-2 small mb-3">Les tarifs saisonniers ont la priorité sur le tarif de base. Utile pour l'été, Noël, vacances scolaires...</div>
                    {tempSeasonalPrices.map((sp, idx) => (
                      <div key={sp.id} className="card bg-light mb-2 p-2">
                        <div className="row g-2 align-items-end">
                          <div className="col-md-3">
                            <label className="form-label small">Libellé</label>
                            <input type="text" className="form-control form-control-sm" placeholder="Ex: Été 2025" value={sp.label} onChange={e => updateSeasonalPrice(idx, 'label', e.target.value)} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small">Du</label>
                            <input type="date" className="form-control form-control-sm" value={sp.startDate} onChange={e => updateSeasonalPrice(idx, 'startDate', e.target.value)} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small">Au</label>
                            <input type="date" className="form-control form-control-sm" value={sp.endDate} onChange={e => updateSeasonalPrice(idx, 'endDate', e.target.value)} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Prix / nuit ({currentConfig.currency})</label>
                            <input type="number" className="form-control form-control-sm" value={sp.pricePerDay} onChange={e => updateSeasonalPrice(idx, 'pricePerDay', parseFloat(e.target.value) || 0)} />
                          </div>
                          <div className="col-md-2">
                            <button className="btn btn-sm btn-outline-danger w-100" onClick={() => removeSeasonalPrice(idx)}>Suppr.</button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button className="btn btn-sm btn-outline-primary mb-4" onClick={addSeasonalPrice}>+ Ajouter une période</button>

                    <h6 className="border-bottom pb-2 mb-2">Réductions séjour long</h6>
                    {tempLongStayDiscounts.map((d, idx) => (
                      <div key={idx} className="d-flex gap-2 align-items-end mb-2">
                        <div>
                          <label className="form-label small">À partir de (nuits)</label>
                          <input type="number" className="form-control form-control-sm" min={1} value={d.minNights} onChange={e => updateLongStayDiscount(idx, 'minNights', parseInt(e.target.value) || 1)} />
                        </div>
                        <div>
                          <label className="form-label small">Réduction (%)</label>
                          <input type="number" className="form-control form-control-sm" min={1} max={80} value={d.discountPercent} onChange={e => updateLongStayDiscount(idx, 'discountPercent', parseFloat(e.target.value) || 0)} />
                        </div>
                        <button className="btn btn-sm btn-outline-danger mb-0" style={{ height: 38 }} onClick={() => removeLongStayDiscount(idx)}>✕</button>
                      </div>
                    ))}
                    <button className="btn btn-sm btn-outline-primary" onClick={addLongStayDiscount}>+ Ajouter une réduction</button>
                  </div>
                )}

                {/* ── RÈGLES ── */}
                {itemFormTab === 'regles' && (
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label small">Check-in à partir de</label>
                      <input type="time" className="form-control" value={currentItem.rules?.checkInTime || '15:00'} onChange={e => updateRules('checkInTime', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Check-out avant</label>
                      <input type="time" className="form-control" value={currentItem.rules?.checkOutTime || '11:00'} onChange={e => updateRules('checkOutTime', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Durée min. (nuits)</label>
                      <input type="number" className="form-control" min={1} value={currentItem.rules?.minNights || ''} onChange={e => updateRules('minNights', parseInt(e.target.value) || undefined)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Durée max. (nuits)</label>
                      <input type="number" className="form-control" min={1} value={currentItem.rules?.maxNights || ''} onChange={e => updateRules('maxNights', parseInt(e.target.value) || undefined)} />
                    </div>
                    {[
                      ['smokingAllowed', '🚬 Fumeurs autorisés'],
                      ['petsAllowed', '🐾 Animaux acceptés'],
                      ['eventsAllowed', '🎉 Événements autorisés'],
                    ].map(([field, label]) => (
                      <div key={field} className="col-md-4">
                        <div className="form-check form-switch mt-2">
                          <input className="form-check-input" type="checkbox" id={`rule-${field}`} checked={!!(currentItem.rules as any)?.[field]} onChange={e => updateRules(field as keyof VacationRules, e.target.checked)} />
                          <label className="form-check-label" htmlFor={`rule-${field}`}>{label}</label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── iCAL ── */}
                {itemFormTab === 'ical' && (
                  <div>
                    <div className="alert alert-info small">
                      <strong>Synchronisation iCal</strong><br />
                      Importez un calendrier Airbnb, Booking.com, etc. pour bloquer automatiquement les dates déjà réservées.
                    </div>
                    <label className="form-label small fw-semibold">URL iCal externe (Airbnb, Booking.com...)</label>
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="https://www.airbnb.com/calendar/ical/xxx.ics"
                      value={currentItem.icalImportUrl || ''}
                      onChange={e => setCurrentItem(p => ({ ...p, icalImportUrl: e.target.value }))}
                    />
                    <div className="text-muted small">
                      <strong>Comment obtenir l'URL iCal :</strong><br />
                      • Airbnb : Calendrier → Synchroniser → Exporter le calendrier<br />
                      • Booking.com : Calendrier → Synchroniser → Exporter<br />
                      • Google Calendar : Paramètres → Intégrer le calendrier → URL secrète iCal
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                  <button className="btn btn-secondary" onClick={() => setShowItemForm(false)}>Annuler</button>
                  <button className="btn btn-primary" onClick={handleSaveItem}>Enregistrer</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TARIFS & CONDITIONS ──────────────────────────────────────────── */}
      {activeTab === 'tarifs' && (
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">💶 Taxe de séjour</h6>
                <p className="text-muted small mb-3">La taxe de séjour est calculée automatiquement dans le total. Elle est affichée séparément dans le récapitulatif.</p>
                <label className="form-label small">Montant par personne / nuit</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    step={0.1}
                    value={currentConfig.touristTaxPerPersonPerNight || 0}
                    onChange={e => updateConfig({ touristTaxPerPersonPerNight: parseFloat(e.target.value) || 0 })}
                  />
                  <span className="input-group-text">{currentConfig.currency}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">🔒 Caution / Dépôt de garantie</h6>
                <div className="mb-3">
                  <label className="form-label small">Montant (0 = désactivé)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={currentConfig.depositAmount || 0}
                      onChange={e => updateConfig({ depositAmount: parseFloat(e.target.value) || 0 })}
                    />
                    <span className="input-group-text">{currentConfig.currency}</span>
                  </div>
                </div>
                {(currentConfig.depositAmount || 0) > 0 && (
                  <>
                    <div className="mb-3">
                      <label className="form-label small">Mode de collecte</label>
                      <select className="form-select" value={currentConfig.depositMode || 'none'} onChange={e => updateConfig({ depositMode: e.target.value as any })}>
                        <option value="none">Aucun (informatif seulement)</option>
                        <option value="preauth">Pré-autorisation bancaire (non débité)</option>
                        <option value="charge">Débit + remboursement après séjour</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label small">Délai de remboursement (jours)</label>
                      <input type="number" className="form-control" min={1} max={90} value={currentConfig.depositRefundDays || 14} onChange={e => updateConfig({ depositRefundDays: parseInt(e.target.value) || 14 })} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">⭐ Avis voyageurs</h6>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="enableReviews" checked={!!currentConfig.enableReviews} onChange={e => updateConfig({ enableReviews: e.target.checked })} />
                  <label className="form-check-label" htmlFor="enableReviews">Activer les avis sur les fiches de bien</label>
                </div>
                <p className="text-muted small mt-2 mb-0">Les voyageurs pourront noter (1-5 étoiles) et commenter leurs séjours.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── PLANNING ─────────────────────────────────────────────────────── */}
      {activeTab === 'planning' && (
        <div>
          <div className="alert alert-info py-2 small mb-3">
            Sélectionnez un bien pour gérer ses disponibilités. Vous pouvez bloquer des dates (travaux, usage personnel, Airbnb) et voir les réservations reçues.
          </div>
          <div className="mb-4">
            <label className="form-label">Sélectionner un bien :</label>
            <select className="form-select" value={selectedPlanningItem?.id || ''} onChange={e => {
              const found = currentConfig.items.find(i => i.id === e.target.value);
              setSelectedPlanningItem(found || null);
            }}>
              <option value="">-- Choisir un bien --</option>
              {currentConfig.items.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>

          {selectedPlanningItem && (
            <div className="card shadow-sm">
              <div className="card-header bg-white"><h6 className="mb-0">Disponibilités : <strong>{selectedPlanningItem.name}</strong></h6></div>
              <div className="card-body">
                {isLoadingReservations && <div className="text-muted small mb-2">Chargement...</div>}
                {reservationsError && <div className="alert alert-warning py-2 small">{reservationsError}</div>}
                {planningItem && (
                  <PlanningCalendar item={planningItem} onBookingClick={(range, dateKey) => { setSelectedBooking(range); setSelectedBookingDate(dateKey); }} />
                )}

                {selectedBooking && (
                  <div className="card border-success mt-3">
                    <div className="card-header bg-success bg-opacity-10"><h6 className="mb-0">Détails réservation</h6></div>
                    <div className="card-body small">
                      <div className="row g-2">
                        <div className="col-md-6"><strong>Période :</strong> {formatLocalDate(selectedBooking.startDate)} → {formatLocalDate(selectedBooking.endDate)}</div>
                        {selectedBooking.reason && <div className="col-12"><strong>Résumé :</strong> {selectedBooking.reason}</div>}
                        {selectedBooking.bookingDetails?.firstName && <div className="col-md-6"><strong>Client :</strong> {selectedBooking.bookingDetails.firstName} {selectedBooking.bookingDetails.lastName}</div>}
                        {selectedBooking.bookingDetails?.email && <div className="col-md-6"><strong>Email :</strong> {selectedBooking.bookingDetails.email}</div>}
                        {selectedBooking.bookingDetails?.phone && <div className="col-md-6"><strong>Tél :</strong> {selectedBooking.bookingDetails.phone}</div>}
                        {selectedBooking.bookingDetails?.totalPrice !== undefined && <div className="col-md-6"><strong>Total :</strong> {selectedBooking.bookingDetails.totalPrice} {selectedBooking.bookingDetails.currency || currentConfig.currency}</div>}
                        {selectedBooking.bookingDetails?.paymentMethod && <div className="col-md-6"><strong>Paiement :</strong> {selectedBooking.bookingDetails.paymentMethod}</div>}
                        {selectedBooking.bookingDetails?.guestsCount && <div className="col-md-6"><strong>Voyageurs :</strong> {selectedBooking.bookingDetails.guestsCount}</div>}
                        {selectedBooking.bookingDetails?.transactionId && <div className="col-12"><strong>Transaction :</strong> {selectedBooking.bookingDetails.transactionId}</div>}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-light p-3 rounded mt-3 border">
                  <h6 className="text-muted small text-uppercase mb-2">Bloquer une période</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3"><label className="form-label small">Début</label><input type="date" className="form-control" value={blockStart} onChange={e => setBlockStart(e.target.value)} /></div>
                    <div className="col-md-3"><label className="form-label small">Fin</label><input type="date" className="form-control" value={blockEnd} onChange={e => setBlockEnd(e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label small">Raison</label><input type="text" className="form-control" placeholder="Ex: Maintenance, Usage personnel..." value={blockReason} onChange={e => setBlockReason(e.target.value)} /></div>
                    <div className="col-md-2"><button className="btn btn-warning w-100 text-dark" onClick={handleAddBlock}>Bloquer</button></div>
                  </div>
                </div>

                <h6 className="text-muted small text-uppercase mt-3 mb-2">Périodes indisponibles</h6>
                {mergedAvailability.length === 0 ? (
                  <div className="text-center text-muted fst-italic py-3">Aucune date bloquée — le bien est 100% disponible.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead className="table-light"><tr><th>Du</th><th>Au</th><th>Type</th><th>Raison</th><th>Action</th></tr></thead>
                      <tbody>
                        {mergedAvailability.map((range, idx) => {
                          const blockedIndex = selectedPlanningItem.availability?.findIndex(r => r.type === 'blocked' && r.startDate === range.startDate && r.endDate === range.endDate && r.reason === range.reason) ?? -1;
                          return (
                            <tr key={idx}>
                              <td>{new Date(range.startDate).toLocaleDateString()}</td>
                              <td>{new Date(range.endDate).toLocaleDateString()}</td>
                              <td>{range.type === 'booking' ? <span className="badge bg-success">Réservation</span> : <span className="badge bg-secondary">Bloqué</span>}</td>
                              <td className="small text-muted">{range.reason || '-'}</td>
                              <td><button className="btn btn-sm btn-outline-danger" disabled={range.type === 'booking'} onClick={() => blockedIndex >= 0 && handleDeleteBlock(blockedIndex)}>🗑️</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── PAIEMENT ─────────────────────────────────────────────────────── */}
      {activeTab === 'payment' && (
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-semibold">Devise</label>
            <input type="text" className="form-control" value={currentConfig.currency} onChange={e => updateConfig({ currency: e.target.value })} placeholder="EUR, USD..." />
          </div>
          <div className="col-12">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" checked={currentConfig.enablePayments} onChange={e => updateConfig({ enablePayments: e.target.checked })} id="enablePay" />
              <label className="form-check-label fw-semibold" htmlFor="enablePay">Activer les paiements en ligne</label>
            </div>
          </div>
          <div className="col-12">
            <h6 className="border-bottom pb-2">Statut des clés API</h6>
            {isLoading ? <p className="text-muted small">Chargement...</p> : (
              <ul className="list-group">
                <li className={`list-group-item ${hasStripeKey ? 'list-group-item-success' : 'list-group-item-warning'}`}>
                  💳 Stripe {hasStripeKey ? '✅ Configuré' : '⚠️ Non configuré — Configurez dans Admin → Clés API'}
                </li>
                <li className={`list-group-item ${hasPaypalKey ? 'list-group-item-success' : 'list-group-item-warning'}`}>
                  🅿️ PayPal {hasPaypalKey ? '✅ Configuré' : '⚠️ Non configuré — Configurez dans Admin → Clés API'}
                </li>
              </ul>
            )}
            <div className="alert alert-info mt-3 small">
              💡 Virement/Sur place est toujours disponible, même sans clé de paiement en ligne.
            </div>
          </div>
        </div>
      )}

      {/* ─── PERSONNALISATION ─────────────────────────────────────────────── */}
      {activeTab === 'custom' && (
        <div className="d-flex flex-column gap-4">

          {/* ── Affichage visiteur ── */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">🖥️ Affichage côté visiteur</h6>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showSearchBar"
                  checked={currentConfig.showSearchBar !== false}
                  onChange={e => updateConfig({ showSearchBar: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="showSearchBar">
                  🔍 Afficher la barre de recherche / filtres
                </label>
              </div>
            </div>
          </div>

          {/* ── Équipements personnalisés ── */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-1">🔧 Équipements personnalisés</h6>
              <p className="text-muted small mb-3">Ajoutez vos propres équipements en complément de la liste standard.</p>
              {(currentConfig.customAmenities || []).map((a, idx) => (
                <div key={a.id} className="d-flex gap-2 align-items-center mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ width: 50, textAlign: 'center' }}
                    placeholder="🏄"
                    value={a.icon}
                    onChange={e => {
                      const updated = [...(currentConfig.customAmenities || [])];
                      updated[idx] = { ...updated[idx], icon: e.target.value };
                      updateConfig({ customAmenities: updated });
                    }}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ex: Surf, Tennis..."
                    value={a.label}
                    onChange={e => {
                      const updated = [...(currentConfig.customAmenities || [])];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      updateConfig({ customAmenities: updated });
                    }}
                  />
                  <button className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => {
                    const updated = (currentConfig.customAmenities || []).filter((_, i) => i !== idx);
                    updateConfig({ customAmenities: updated });
                  }}>✕</button>
                </div>
              ))}
              <button className="btn btn-sm btn-outline-primary mt-1" onClick={() => {
                const newA: AmenityDef = { id: `custom_${Date.now()}`, icon: '⭐', label: '' };
                updateConfig({ customAmenities: [...(currentConfig.customAmenities || []), newA] });
              }}>+ Ajouter un équipement</button>
            </div>
          </div>

          {/* ── Badges personnalisés ── */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-1">🏷️ Badges personnalisés</h6>
              <p className="text-muted small mb-3">Créez vos propres badges en complément des badges standard (Nouveauté, Coup de cœur…).</p>
              {(currentConfig.customBadges || []).map((b, idx) => (
                <div key={b.id} className="d-flex gap-2 align-items-center mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Libellé"
                    value={b.label}
                    onChange={e => {
                      const updated = [...(currentConfig.customBadges || [])];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      updateConfig({ customBadges: updated });
                    }}
                  />
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <label className="form-label small mb-0">Fond</label>
                    <input
                      type="color"
                      className="form-control form-control-color form-control-sm"
                      style={{ width: 38, height: 32 }}
                      value={b.bg}
                      onChange={e => {
                        const updated = [...(currentConfig.customBadges || [])];
                        updated[idx] = { ...updated[idx], bg: e.target.value };
                        updateConfig({ customBadges: updated });
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <label className="form-label small mb-0">Texte</label>
                    <input
                      type="color"
                      className="form-control form-control-color form-control-sm"
                      style={{ width: 38, height: 32 }}
                      value={b.color}
                      onChange={e => {
                        const updated = [...(currentConfig.customBadges || [])];
                        updated[idx] = { ...updated[idx], color: e.target.value };
                        updateConfig({ customBadges: updated });
                      }}
                    />
                  </div>
                  <span className="badge flex-shrink-0" style={{ backgroundColor: b.bg, color: b.color }}>{b.label || 'Aperçu'}</span>
                  <button className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => {
                    const updated = (currentConfig.customBadges || []).filter((_, i) => i !== idx);
                    updateConfig({ customBadges: updated });
                  }}>✕</button>
                </div>
              ))}
              <button className="btn btn-sm btn-outline-primary mt-1" onClick={() => {
                const newB: CustomBadge = { id: `badge_${Date.now()}`, label: 'Nouveau', bg: '#6366f1', color: '#ffffff' };
                updateConfig({ customBadges: [...(currentConfig.customBadges || []), newB] });
              }}>+ Ajouter un badge</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CONFIRMATIONS ────────────────────────────────────────────────── */}
      {activeTab === 'confirmations' && (
        <div className="d-flex flex-column gap-4">
          <div className="alert alert-info small">
            📧 Configurez les emails envoyés automatiquement lors d'une réservation ou d'une annulation.
            Utilisez <code>{'{{guestName}}'}</code>, <code>{'{{itemName}}'}</code>, <code>{'{{startDate}}'}</code>, <code>{'{{endDate}}'}</code>, <code>{'{{totalPrice}}'}</code>, <code>{'{{currency}}'}</code> dans les templates.
          </div>

          {/* Notifications propriétaire */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">🔔 Notifications propriétaire</h6>
              <div className="row g-3">
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="ownerEmailOnBooking"
                      checked={!!currentConfig.notificationSettings?.ownerEmailOnBooking}
                      onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, ownerEmailOnBooking: e.target.checked } })} />
                    <label className="form-check-label" htmlFor="ownerEmailOnBooking">Email au propriétaire à chaque nouvelle réservation</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="ownerEmailOnCancellation"
                      checked={!!currentConfig.notificationSettings?.ownerEmailOnCancellation}
                      onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, ownerEmailOnCancellation: e.target.checked } })} />
                    <label className="form-check-label" htmlFor="ownerEmailOnCancellation">Email au propriétaire en cas d'annulation</label>
                  </div>
                </div>
                {currentConfig.notificationSettings?.ownerEmailOnBooking && (
                  <>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Objet email (propriétaire)</label>
                      <input type="text" className="form-control" style={{ height: 38 }}
                        placeholder="Nouvelle réservation — {{itemName}}"
                        value={currentConfig.notificationSettings?.ownerBookingEmailSubject || ''}
                        onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, ownerBookingEmailSubject: e.target.value } })} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Corps email (propriétaire)</label>
                      {renderEditor(currentConfig.notificationSettings?.ownerBookingEmailBody || '', v => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, ownerBookingEmailBody: v } }), 5)}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Confirmations voyageur */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">✉️ Confirmations voyageur</h6>
              <div className="row g-3">
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="guestEmailOnBooking"
                      checked={!!currentConfig.notificationSettings?.guestEmailOnBooking}
                      onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, guestEmailOnBooking: e.target.checked } })} />
                    <label className="form-check-label" htmlFor="guestEmailOnBooking">Email de confirmation au voyageur</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="guestEmailOnCancellation"
                      checked={!!currentConfig.notificationSettings?.guestEmailOnCancellation}
                      onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, guestEmailOnCancellation: e.target.checked } })} />
                    <label className="form-check-label" htmlFor="guestEmailOnCancellation">Email d'annulation au voyageur</label>
                  </div>
                </div>
                {currentConfig.notificationSettings?.guestEmailOnBooking && (
                  <>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Objet email (voyageur)</label>
                      <input type="text" className="form-control" style={{ height: 38 }}
                        placeholder="Votre réservation est confirmée — {{itemName}}"
                        value={currentConfig.notificationSettings?.guestBookingEmailSubject || ''}
                        onChange={e => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, guestBookingEmailSubject: e.target.value } })} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Corps email (voyageur)</label>
                      {renderEditor(currentConfig.notificationSettings?.guestBookingEmailBody || '', v => updateConfig({ notificationSettings: { ...currentConfig.notificationSettings, guestBookingEmailBody: v } }), 5)}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── AVIS ─────────────────────────────────────────────────────────── */}
      {activeTab === 'avis' && (
        <div>
          {!currentConfig.enableReviews ? (
            <div className="alert alert-warning">
              Les avis voyageurs sont désactivés. Activez-les dans l'onglet <strong>Tarifs & Conditions</strong>.
            </div>
          ) : (
            <div>
              <div className="alert alert-info small">
                Les avis laissés par les voyageurs apparaissent ici. Vous pouvez y répondre pour chaque avis.
              </div>
              <p className="text-muted text-center py-4 fst-italic">Fonctionnalité disponible prochainement.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
